import { api, ApiError } from '../api.js';
import { el, clear, fragment } from '../dom.js';
import { buildMonth } from '../../server/lib/calendar.js';
import { t, WEEKDAY_SHORT, monthLabel } from '../i18n.js';
import { openSeriesForm } from './series-form.js';
import { openOccurrenceForm } from './occurrence-form.js';
import '../../public/calendar.css';

// A section of the Dates page rather than a page of its own: the calendar and
// the series list are two readings of the same programme, and putting them on
// two addresses meant a venue edited a series on one screen and checked what it
// did on another.
export async function renderCalendar({ accountId, query, basePath = '/panel' }) {
  const venueId = accountId;
  const { year, month } = monthFromQuery(query);

  const from = `${year}-${pad(month)}-01`;
  const occurrences = await api.get(
    `/accounts/${venueId}/occurrences?from=${from}&to=${lastDayOf(year, month)}`
  );

  // The month is held in memory so a move can repaint immediately and roll
  // back on refusal, rather than waiting on a round trip to show anything.
  const view = { venueId, basePath, year, month, occurrences, picking: null, body: el('div') };

  paint(view);
  wire(view);

  // The node *and* a way to refresh it. Without the second half the grid was
  // painted once and never again: deleting an event, publishing one, pausing or
  // shifting a series all refetched the list below and left the month above
  // showing what used to be true. The page owns both, so the page needs both.
  const node = fragment([header(view), view.body]);
  node.reload = () => reload(view);
  return node;
}

// Which day the open event panel is about, marked on whatever grid is on the
// page. Registered once for the life of the app rather than per render: it
// reads the document each time, so there is nothing to keep in step and
// nothing to leak when a view is replaced.
//
// Cleared on close, because a day left marked after the panel is gone is a
// state with nothing behind it.
document.addEventListener('tba:event-date', (event) => {
  for (const day of document.querySelectorAll('.cal-day-chosen')) {
    day.classList.remove('cal-day-chosen');
  }
  const date = event.detail?.date;
  if (!date) return;
  document.querySelector(`.cal-day[data-date="${date}"]`)?.classList.add('cal-day-chosen');
});

// Refetch the month. Used after a create, where the client cannot know what
// the materialiser produced — a series can yield several occurrences.
async function reload(view, message) {
  const from = `${view.year}-${pad(view.month)}-01`;
  view.occurrences = await api.get(
    `/accounts/${view.venueId}/occurrences?from=${from}&to=${lastDayOf(view.year, view.month)}`
  );
  paint(view);
  if (message) announce(view, message);
}

function paint(view) {
  const { weeks } = buildMonth({
    year: view.year,
    month: view.month,
    occurrences: view.occurrences,
    today: todayIso(),
  });

  // A repaint replaces every node, so whatever had focus is gone. Without
  // this, moving an event with the keyboard dropped focus to the document and
  // the next Tab started from the top of the page.
  const focused = document.activeElement;
  const refocus = focused && view.body.contains(focused)
    ? { occurrence: focused.dataset?.occurrence, date: focused.dataset?.date }
    : null;

  clear(view.body);
  view.body.append(
    live(view),
    weekdayRow(),
    el('div', { class: 'cal-grid' }, weeks.map(weekRow)),
  );
  if (view.occurrences.length === 0) {
    view.body.append(el('p', { class: 'pub-empty' }, t.noEventsThisMonth));
  }
  if (view.picking) markPicking(view);

  if (refocus?.occurrence) {
    view.body.querySelector(`.cal-chip[data-occurrence="${refocus.occurrence}"]`)?.focus();
  } else if (refocus?.date) {
    view.body.querySelector(`.cal-day[data-date="${refocus.date}"]`)?.focus();
  }
}

// ------------------------------------------------------------ interaction

// The listeners `wire()` puts on `document` belong to one mounted month, and
// the router builds a fresh one every time somebody comes back to the calendar.
// They were anonymous and never removed, so each visit left another handler
// behind holding that visit's `view` — its month, its occurrences, its detached
// nodes. Counted in the browser: four round trips through /panel/beteiligt and
// back, four document keydown listeners. The panel is a tool people keep open
// in a tab all evening, so "small and linear" is the wrong shape here.
//
// One controller per mount, aborted by the next. Anything hung on `document`
// from inside wire() takes the signal and disappears with the mount that made
// it; anything on `view.body` needs none, because the node itself goes.
let mounted = null;

function wire(view) {
  mounted?.abort();
  mounted = new AbortController();
  const { signal } = mounted;

  view.body.addEventListener('click', async (event) => {
    const chipEl = event.target.closest('.cal-chip');
    const dayEl = event.target.closest('.cal-day');

    if (view.picking) {
      // Any click while picking resolves it: a day completes the move,
      // anything else cancels. No silent third state.
      const target = dayEl?.dataset.date;
      const occurrence = view.picking;
      stopPicking(view);
      if (target) await moveOccurrence(view, occurrence, target);
      return;
    }

    if (chipEl) {
      openMenu(view, byId(view, Number(chipEl.dataset.occurrence)), chipEl);
      return;
    }

    if (dayEl) {
      await openSeriesForm({
        venueId: view.venueId,
        date: dayEl.dataset.date,
        onSaved: (message) => reload(view, message),
      });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeMenu();
    if (view.picking) stopPicking(view);
  }, { signal });

  // A day cell is a div, so the browser does not synthesise a click from
  // Space. Without this the whole two-step move was mouse-only, and the menu
  // entry that starts it is reachable by keyboard — an invitation into a dead
  // end.
  view.body.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const dayEl = event.target.closest?.('.cal-day');
    if (!dayEl || !view.body.contains(dayEl)) return;
    event.preventDefault();
    dayEl.click();
  });

  attachDragging(view);
}

// Mouse only, and deliberately: a month grid at 375px has cells too small for
// a precision gesture, and dragging there fights the page scroll. Touch keeps
// the two-step flow, which is not a downgrade — it is the same number of
// deliberate actions without the aiming.
//
// The drop calls moveOccurrence(), the same function the two-step gesture
// uses, so there is one write, one conflict check and one rollback.
function attachDragging(view) {
  view.body.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const chipEl = event.target.closest('.cal-chip');
    if (!chipEl || view.picking) return;

    const occurrence = byId(view, Number(chipEl.dataset.occurrence));
    if (!occurrence) return;

    const origin = { x: event.clientX, y: event.clientY };
    let dropDay = null;
    let dragging = false;

    const onMove = (moveEvent) => {
      if (!dragging) {
        // A few pixels of slop, or every click would register as a drag and
        // the menu would never open.
        if (Math.hypot(moveEvent.clientX - origin.x, moveEvent.clientY - origin.y) < 4) return;
        dragging = true;
        chipEl.classList.add('cal-chip-dragging');
      }
      const over = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY)?.closest('.cal-day');
      if (over === dropDay) return;
      dropDay?.classList.remove('cal-day-drop');
      dropDay = over;
      dropDay?.classList.add('cal-day-drop');
    };

    const onUp = async () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      chipEl.classList.remove('cal-chip-dragging');
      dropDay?.classList.remove('cal-day-drop');

      // Not a drag, or dropped where it already was: leave it to the click
      // handler, which opens the menu.
      if (!dragging || !dropDay) return;
      await moveOccurrence(view, occurrence, dropDay.dataset.date);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  });
}

function startPicking(view, occurrence) {
  view.picking = occurrence;
  markPicking(view);
  announce(view, t.pickTarget);
}

function markPicking(view) {
  view.body.querySelector('.cal-grid')?.classList.add('cal-picking');
  view.body.querySelector(`[data-occurrence="${view.picking.id}"]`)?.classList.add('cal-chip-selected');

  // Day cells are plain divs the rest of the time — forty focus stops nobody
  // wants to tab through. While a move is in progress they *are* the control,
  // so they become reachable, named, and answer Enter and Space.
  for (const day of view.body.querySelectorAll('.cal-day')) {
    day.tabIndex = 0;
    day.setAttribute('role', 'button');
    day.setAttribute('aria-label', t.moveTo(day.dataset.date));
  }
  view.body.querySelector(`.cal-day[data-date="${view.picking.localDate}"]`)?.focus();
}

function stopPicking(view) {
  view.picking = null;
  view.body.querySelector('.cal-grid')?.classList.remove('cal-picking');
  view.body.querySelector('.cal-chip-selected')?.classList.remove('cal-chip-selected');
  for (const day of view.body.querySelectorAll('.cal-day')) {
    day.removeAttribute('tabindex');
    day.removeAttribute('role');
    day.removeAttribute('aria-label');
  }
  dismissHint(view);
}

// One code path for both gestures. The two-step selection calls this and so
// does the drag handler, so there is a single write, a single conflict check
// and a single rollback.
export async function moveOccurrence(view, occurrence, targetDate) {
  if (targetDate === occurrence.localDate) return;
  if (targetDate < todayIso() && !window.confirm(t.confirmPast)) return;

  const before = {
    localDate: occurrence.localDate,
    startsAtUtc: occurrence.startsAtUtc,
    endsAtUtc: occurrence.endsAtUtc,
    status: occurrence.status,
  };

  occurrence.localDate = targetDate;
  occurrence.status = 'moved';
  paint(view);

  try {
    const updated = await api.patch(`/occurrences/${occurrence.id}`, {
      localDate: targetDate,
      expectedStartsAtUtc: before.startsAtUtc,
    });
    Object.assign(occurrence, updated);
    paint(view);
  } catch (error) {
    Object.assign(occurrence, before);
    paint(view);
    announce(view, moveMessage(error));
  }

  // Follow the thing that moved. paint()'s own restore cannot help here: it
  // remembers the day cell that had focus, and that cell stops being focusable
  // the moment picking ends — so a keyboard move landed focus nowhere.
  view.body.querySelector(`.cal-chip[data-occurrence="${occurrence.id}"]`)?.focus();
}

async function toggleCancelled(view, occurrence) {
  const cancelled = occurrence.status === 'cancelled';
  const before = occurrence.status;

  occurrence.status = cancelled ? 'scheduled' : 'cancelled';
  paint(view);

  try {
    const updated = await api.patch(`/occurrences/${occurrence.id}`, {
      status: cancelled ? 'scheduled' : 'cancelled',
    });
    Object.assign(occurrence, updated);
    paint(view);
  } catch {
    occurrence.status = before;
    paint(view);
    announce(view, t.cancelFailed);
  }
}

function moveMessage(error) {
  if (!(error instanceof ApiError)) return t.moveFailed;
  if (error.code === 'conflict') return t.moveConflict;
  if (error.code === 'occupied') return t.moveOccupied;
  return t.moveFailed;
}

// ------------------------------------------------------------------- menu

function openMenu(view, occurrence, anchorEl) {
  closeMenu();
  if (!occurrence) return;

  const cancelled = occurrence.status === 'cancelled';
  const item = (label) => el('button', { type: 'button', role: 'menuitem' }, label);
  const moveButton = item(t.move);
  const cancelButton = item(cancelled ? t.reactivate : t.cancelEvent);
  const editButton = item(t.edit);
  // A one-off has no other dates to deviate from — "Nur dieser Termin" would
  // be the exact same edit, so it only appears on repeating series.
  const isSingle = !occurrence.recurrence_json || (() => {
    try { return JSON.parse(occurrence.recurrence_json).freq === 'once'; } catch { return true; }
  })();
  const onlyThisButton = isSingle ? null : item(t.editOccurrence);
  // role="menu" needs menuitem children and a name, or it announces as an
  // unlabelled group of buttons with no indication of what they act on.
  const menu = el('div', { class: 'cal-menu', role: 'menu', 'aria-label': occurrence.title },
    [moveButton, cancelButton, onlyThisButton, editButton].filter(Boolean));

  // Where focus goes when the menu closes. Without it, dismissing the menu
  // dropped focus to the document body and the next Tab started over.
  menu.dataset.returnTo = occurrence.id;

  if (onlyThisButton) onlyThisButton.addEventListener('click', async () => {
    closeMenu();
    await openOccurrenceForm({ occurrence, onSaved: (message) => reload(view, message) });
  });

  editButton.addEventListener('click', async () => {
    closeMenu();
    const record = await api.get(`/series/${occurrence.seriesId}`);
    await openSeriesForm({
      venueId: view.venueId,
      series: record,
      onSaved: (message) => reload(view, message),
    });
  });

  moveButton.addEventListener('click', () => {
    // No focus restore: startPicking() moves focus onto the day grid, which is
    // where the next action happens.
    closeMenu({ restoreFocus: false });
    startPicking(view, occurrence);
  });
  cancelButton.addEventListener('click', async () => {
    closeMenu();
    await toggleCancelled(view, occurrence);
  });

  const box = anchorEl.getBoundingClientRect();
  menu.style.left = `${Math.round(box.left + window.scrollX)}px`;
  menu.style.top = `${Math.round(box.bottom + window.scrollY + 2)}px`;
  document.body.append(menu);
  moveButton.focus();

  // Deferred, or the click that opened the menu closes it again.
  setTimeout(() => {
    document.addEventListener('click', outsideClick, { once: false });
  }, 0);
}

function outsideClick(event) {
  const menu = document.querySelector('.cal-menu');
  if (menu && !menu.contains(event.target)) closeMenu();
}

function closeMenu({ restoreFocus = true } = {}) {
  const menu = document.querySelector('.cal-menu');
  const returnTo = menu?.dataset.returnTo;
  menu?.remove();
  document.removeEventListener('click', outsideClick);

  if (restoreFocus && returnTo) {
    document.querySelector(`.cal-chip[data-occurrence="${returnTo}"]`)?.focus();
  }
}

// ------------------------------------------------------------------ hints

// One region, created once and kept across repaints. A role="status" element
// inserted at the same moment as its text is a coin flip: assistive tech
// watches regions that already exist, so a fresh node often announces nothing.
function live(view) {
  if (!view.hint) {
    view.hint = el('p', { class: 'cal-hint', role: 'status', 'aria-live': 'polite' });
    view.hint.hidden = true;
  }
  return view.hint;
}

function announce(view, message) {
  const region = live(view);
  region.hidden = false;
  region.textContent = message;
}

function dismissHint(view) {
  if (!view.hint) return;
  view.hint.textContent = '';
  view.hint.hidden = true;
}

// ---------------------------------------------------------------- chrome

function header(view) {
  const href = (y, m) => `${view.basePath}?month=${y}-${pad(m)}`;
  const [prevYear, prevMonth] = shiftMonth(view.year, view.month, -1);
  const [nextYear, nextMonth] = shiftMonth(view.year, view.month, 1);
  const now = new Date();

  return el('div', { class: 'cal-head' }, [
    el('h2', { class: 'cal-month' }, monthLabel(view.year, view.month)),
    el('nav', { class: 'cal-nav' }, [
      el('a', { class: 'button button-quiet', href: href(prevYear, prevMonth), 'aria-label': t.previousMonth }, '←'),
      el('a', { class: 'button button-quiet', href: href(now.getUTCFullYear(), now.getUTCMonth() + 1) }, t.toToday),
      el('a', { class: 'button button-quiet', href: href(nextYear, nextMonth), 'aria-label': t.nextMonth }, '→'),
    ]),
  ]);
}

function weekdayRow() {
  return el('div', { class: 'cal-weekdays' },
    WEEKDAY_SHORT.map((name) => el('div', { class: 'cal-weekday' }, name)));
}

// The day cells are the background and span every lane; the chips sit on top
// of them in rows 2..n. That is what keeps a multi-day bar able to cross
// columns while each entry still appears *inside* its day rather than in a
// separate strip underneath.
// Placement is classes, never an inline style. style-src 'self' governs style
// *attributes* too, so the geometry would be dropped in production and the
// grid would collapse — while working perfectly on localhost, where the dev
// policy relaxes.
function weekRow(week) {
  const days = week.days.map((day, column) => el('div', {
    class: [
      'cal-day',
      `cal-c${column + 1}`,
      day.inMonth ? '' : 'cal-day-out',
      day.isToday ? 'cal-day-today' : '',
    ].filter(Boolean).join(' '),
    dataset: { date: day.date },
  }, el('span', { class: 'cal-day-number' }, String(Number(day.date.slice(8))))));

  return el('div', {
    class: `cal-week cal-lanes-${Math.min(week.laneCount, 8)}`,
  }, [...days, ...week.segments.map(chip)]);
}

function chip(segment) {
  const { occurrence } = segment;
  const classes = [
    'cal-chip',
    `cal-c${segment.startColumn}`,
    `cal-s${Math.min(segment.span, 7)}`,
    `cal-r${Math.min(segment.lane + 1, 8)}`,
  ];
  if (occurrence.status === 'cancelled') classes.push('cal-chip-cancelled');
  if (occurrence.status === 'moved') classes.push('cal-chip-moved');
  if (occurrence.seriesStatus === 'draft') classes.push('cal-chip-draft');
  if (segment.continuesRight) classes.push('cal-chip-open-right');
  if (segment.continuesLeft) classes.push('cal-chip-open-left');

  const label = segment.continuesLeft
    ? occurrence.title
    : `${occurrence.localStartTime ?? ''} ${occurrence.title}`.trim();

  return el('button', {
    type: 'button',
    class: classes.join(' '),
    dataset: { occurrence: occurrence.id },
    title: occurrence.title,
  }, label);
}

// --------------------------------------------------------------- helpers

function byId(view, id) {
  return view.occurrences.find((row) => row.id === id) ?? null;
}

function monthFromQuery(query) {
  const parsed = /^(\d{4})-(\d{2})$/.exec(query?.get('month') ?? '');
  if (parsed) return { year: Number(parsed[1]), month: Number(parsed[2]) };
  const now = new Date();
  return { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 };
}

function shiftMonth(year, month, delta) {
  const index = year * 12 + (month - 1) + delta;
  return [Math.floor(index / 12), (index % 12) + 1];
}

function lastDayOf(year, month) {
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

const pad = (n) => String(n).padStart(2, '0');
